/**
 * Date + bookable-slot picker, shared by BookAppointmentModal and
 * RescheduleModal.
 *
 * Slots come from GET /v1/diary/slots?date=YYYY-MM-DD[&consultantId&type],
 * which returns [{ consultantId, startsAt, endsAt }] — the union of every
 * active consultant schedule minus blocks and existing appointments. Each
 * slot already carries its consultantId, so booking never needs the
 * super-admin-only /v1/users list.
 *
 * When `consultantId` is supplied (reschedule — the backend keeps the
 * original consultant) the picker is scoped to that consultant only.
 */
import { useEffect, useMemo, useState } from 'react';
import { formatInOrgTz } from '../lib/datetime';
import { Link } from 'react-router-dom';
import { appointmentsService } from '../lib/services';
import { apiErrorMessage } from '../lib/apiError';
import { useConsultantOptions } from '../lib/useDirectory';

const todayISO = () => new Date().toISOString().slice(0, 10);

const fmtTime = (iso) =>
  formatInOrgTz(iso, { hour: '2-digit', minute: '2-digit', hour12: false });

export default function SlotPicker({
  date,
  onDateChange,
  type,
  consultantId,
  value,          // selected slot object or null
  onSelect,
  disabled,
}) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Resolve consultant ids to names for the per-consultant slot groups
  // (only needed when the picker isn't scoped to one consultant).
  const { options: consultantOptions } = useConsultantOptions();
  const consultantNameOf = useMemo(() => {
    const m = new Map(consultantOptions.map(o => [o.id, o.primary]));
    return (id) => m.get(id) || `Consultant ${String(id).slice(0, 8)}`;
  }, [consultantOptions]);

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = { date };
    if (type) params.type = type;
    if (consultantId) params.consultantId = consultantId;

    appointmentsService.slots(params)
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        // Drop slots already in the past — the backend validates startsAt,
        // so offering them would just produce a 400.
        const now = Date.now();
        setSlots(list.filter(s => new Date(s.startsAt).getTime() > now));
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Could not load slots'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [date, type, consultantId]);

  // Clear a selection that's no longer on offer for the new date.
  useEffect(() => {
    if (value && !slots.some(s => s.startsAt === value.startsAt && s.consultantId === value.consultantId)) {
      onSelect?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const key = s.consultantId || 'any';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return [...map.entries()];
  }, [slots]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="form-field">
        <label htmlFor="sp-date">Date</label>
        <input
          id="sp-date"
          type="date"
          value={date}
          min={todayISO()}
          disabled={disabled}
          onChange={(e) => onDateChange(e.target.value)}
        />
      </div>

      <div>
        <label
          className="mono text-xs text-muted"
          style={{ display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.6 }}
        >
          Available slots {loading ? '· loading…' : `· ${slots.length}`}
        </label>

        {error && <div className="text-xs" style={{ color: 'var(--danger)' }}>{error}</div>}

        {!loading && !error && slots.length === 0 && (
          <div className="text-xs text-muted" style={{ lineHeight: 1.6 }}>
            No bookable slots on this date. Try another day, or set up{' '}
            <Link to="/diary-settings" style={{ color: 'var(--accent-primary-hover)' }}>
              working hours
            </Link>{' '}
            — a consultant needs an active weekly schedule before their slots appear.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 220, overflowY: 'auto' }}>
          {grouped.map(([cid, list]) => (
            <div key={cid}>
              {!consultantId && (
                <div className="mono text-xs text-muted" style={{ marginBottom: 4 }}>
                  {consultantNameOf(cid)}
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {list.map((s) => {
                  const selected = value?.startsAt === s.startsAt && value?.consultantId === s.consultantId;
                  return (
                    <button
                      key={`${s.consultantId}-${s.startsAt}`}
                      type="button"
                      disabled={disabled}
                      className={`pill ${selected ? 'active filled' : ''}`}
                      style={{ cursor: 'pointer', border: '1px solid var(--border-color)' }}
                      aria-pressed={selected}
                      onClick={() => onSelect(selected ? null : s)}
                    >
                      {fmtTime(s.startsAt)}–{fmtTime(s.endsAt)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export { todayISO, fmtTime };
